<!--
  CalendarDayCell:F-0 月历单格(UI-SPEC §5.1.c 6 variant)
  baseball / concert / hot → 进兑换;wish → 进 wishlist 详情;sold-out / empty → 不可点
  H5 rem 基准(100px=1rem);PC 直写 px。无 font-family 声明(§4.9)。
-->
<template>
  <div
    class="day-cell"
    :class="[
      `variant-${variant}`,
      `page-${pageType}`,
      { clickable: isClickable },
    ]"
    @click="onClick"
  >
    <!-- 日期数字:月外补齐格不显示;当月空场次仍显示日期 -->
    <div v-if="cell.isCurrentMonth" class="day-number">
      {{ cell.dayOfMonth }}
    </div>

    <!-- baseball:对手缩写(白) -->
    <div
      v-if="variant === 'baseball'"
      class="sub-label team-label"
      :class="{ 'is-multiline': teamLabelLines.length > 1 }"
      :style="teamLabelStyle"
    >
      <span
        v-for="(line, lineIndex) in teamLabelLines"
        :key="lineIndex"
      >
        {{ line }}
      </span>
    </div>

    <!-- hot:对手缩写 + 火 icon；concert-hot 保留 concert 底图和文案 -->
    <template v-else-if="variant === 'hot' || variant === 'concert-hot'">
      <div
        v-if="variant === 'hot'"
        class="sub-label team-label"
        :class="{ 'is-multiline': teamLabelLines.length > 1 }"
        :style="teamLabelStyle"
      >
        <span
          v-for="(line, lineIndex) in teamLabelLines"
          :key="lineIndex"
        >
          {{ line }}
        </span>
      </div>
      <div v-else class="sub-label">Concert</div>
      <span class="hot-icon" aria-hidden="true"></span>
    </template>

    <!-- wish:点击后打开 wishlist 详情弹窗；concert-wish 保留 concert 底图和文案 -->
    <template v-else-if="variant === 'wish' || variant === 'concert-wish'">
      <div
        v-if="variant === 'wish'"
        class="sub-label team-label"
        :class="{ 'is-multiline': teamLabelLines.length > 1 }"
        :style="teamLabelStyle"
      >
        <span
          v-for="(line, lineIndex) in teamLabelLines"
          :key="lineIndex"
        >
          {{ line }}
        </span>
      </div>
      <div v-else class="sub-label">Concert</div>
      <span class="wish-tag" aria-hidden="true">wish</span>
    </template>

    <!-- concert:固定文字 Concert -->
    <div v-else-if="variant === 'concert'" class="sub-label">Concert</div>

    <!-- sold-out(含 wish 降级):对手缩写灰 -->
    <div
      v-else-if="variant === 'sold-out'"
      class="sub-label team-label sold-out-text"
      :class="{ 'is-multiline': teamLabelLines.length > 1 }"
      :style="teamLabelStyle"
    >
      <span
        v-for="(line, lineIndex) in teamLabelLines"
        :key="lineIndex"
      >
        {{ line }}
      </span>
    </div>

    <!-- empty:仅背景色,不渲染任何文字 -->
  </div>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
import type { CalendarCell, CellVariant } from '../utils/calendar';
type Props = {
  cell: CalendarCell;
  variant: CellVariant;
  pageType: 'PC' | 'H5';
};
const __sfc_main = {};
__sfc_main.props = {
  cell: {
    key: "cell",
    required: true,
    type: null
  },
  variant: {
    key: "variant",
    required: true,
    type: null
  },
  pageType: {
    key: "pageType",
    required: true,
    type: String
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const isClickable = computed(() => props.variant === 'baseball' || props.variant === 'concert' || props.variant === 'concert-hot' || props.variant === 'concert-wish' || props.variant === 'hot' || props.variant === 'wish');
  const teamLabel = computed<string>(() => {
    if (!props.cell.event) return '';
    return normalizeTeamLabel(props.cell.event.opponentShort || props.cell.event.opponentFull || '');
  });
  const teamLabelLines = computed<string[]>(() => {
    const lines = teamLabel.value.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    return lines.length > 0 ? lines : [''];
  });
  function visualLength(text: string): number {
    let total = 0;
    for (const char of text.trim()) {
      total += /[\u3400-\u9fff]/.test(char) ? 1.8 : 1;
    }
    return total;
  }
  function normalizeTeamLabel(text: string): string {
    return text.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '').trim();
  }
  function maxLineLength(lines: string[]): number {
    return Math.max(...lines.map(line => visualLength(line)), 0);
  }
  function teamFontSize(lines: string[], pageType: 'PC' | 'H5'): string {
    const len = maxLineLength(lines);
    if (pageType === 'H5') {
      if (len <= 3) return '0.34rem';
      if (len <= 4.5) return '0.3rem';
      if (len <= 6.5) return '0.25rem';
      return '0.16rem';
    }
    if (len <= 3) return '22px';
    if (len <= 4.5) return '19px';
    if (len <= 6.5) return '16px';
    return '11px';
  }
  const teamLabelStyle = computed<Record<string, string>>(() => ({
    fontSize: teamFontSize(teamLabelLines.value, props.pageType)
  }));
  function onClick(): void {
    if (!isClickable.value) return;
    emit('click', props.cell);
  }
  return {
    isClickable,
    teamLabelLines,
    teamLabelStyle,
    onClick
  };
};
export default __sfc_main;
</script>

<style scoped>
.day-cell {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}
.day-number {
  position: absolute;
  color: #ffffff;
  font-weight: 400;
  font-family: "Avenir Next Condensed", "HelveticaNeue-CondensedBold", "Arial Narrow", "Roboto Condensed", sans-serif-condensed, sans-serif;
  font-stretch: condensed;
}
.sub-label {
  color: #ffffff;
  font-weight: 600;
  text-align: center;
  font-family: "Avenir Next Condensed", "HelveticaNeue-CondensedBold", "Arial Narrow", "Roboto Condensed", sans-serif-condensed, sans-serif;
  font-stretch: condensed;
}
.team-label {
  position: absolute;
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
  letter-spacing: -0.03em;
  transform: translateX(-50%);
}
.team-label span {
  display: block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
}
.sold-out-text {
  color: #4880dd;
  opacity: 0.7;
}
.hot-icon {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eb203d;
  box-sizing: border-box;
}
.hot-icon::after {
  content: '';
  display: block;
  background: url('../assets/icon-calendar-hot.svg') center / contain no-repeat;
}
.wish-tag {
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0041ac;
  color: #ffffff;
  font-weight: 400;
  font-family: "Avenir Next Condensed", "HelveticaNeue-CondensedBold", "Arial Narrow", "Roboto Condensed", sans-serif-condensed, sans-serif;
  font-stretch: condensed;
  line-height: 1em;
  box-sizing: border-box;
}
.clickable {
  cursor: pointer;
}

/* ─── variant 背景色(UI-SPEC §5.1.c) ───────────────────── */
.variant-baseball {
  background: #ffffff;
}
.variant-concert,
.variant-concert-hot,
.variant-concert-wish {
  background: #ff6900 url('../assets/bg-calendar-concert-cell.png') center / cover no-repeat;
}
.variant-hot {
  background: #ffffff;
}
.variant-sold-out {
  background: #0041ac;
}
.variant-wish {
  background: #ffffff;
}
.variant-empty {
  background: #072b64;
}
.variant-baseball .day-number,
.variant-baseball .sub-label,
.variant-hot .day-number,
.variant-hot .sub-label,
.variant-wish .day-number,
.variant-wish .sub-label {
  color: #0041ac;
}
.variant-empty .day-number,
.variant-sold-out .day-number {
  color: #0041ac;
}
.variant-sold-out .sub-label {
  color: #4880dd;
}

/* ─── H5(rem)UI-SPEC §5.1.b 92×92 ─────────────────────── */
.page-H5.day-cell {
  width: 0.92rem;
  height: 0.92rem;
  padding: 0.06rem;
}
.page-H5 .day-number {
  top: 0.04rem;
  right: 0.06rem;
  font-size: 0.22rem;
  line-height: 1em;
}
.page-H5 .sub-label {
  line-height: 1em;
}
.page-H5 .team-label {
  max-width: 0.78rem;
  bottom: 0.26rem;
  gap: 0.01rem;
}
.page-H5 .team-label.is-multiline {
  bottom: 0.13rem;
}
.page-H5.variant-concert .sub-label,
.page-H5.variant-concert-hot .sub-label,
.page-H5.variant-concert-wish .sub-label {
  position: absolute;
  left: 50%;
  bottom: 0.26rem;
  max-width: 0.84rem;
  overflow: hidden;
  white-space: nowrap;
  font-size: 0.2rem;
  font-weight: 600;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  transform: translateX(-50%);
}
.page-H5 .team-label.is-multiline {
  gap: 0.01rem;
}
.page-H5 .hot-icon {
  left: 0;
  top: 0;
  width: 0.22rem;
  height: 0.22rem;
  border-radius: 0 0 0.04rem 0;
}
.page-H5 .hot-icon::after {
  width: 0.1375rem;
  height: 0.165rem;
}
.page-H5 .wish-tag {
  width: 0.46rem;
  height: 0.22rem;
  border-radius: 0 0 0.04rem 0;
  font-size: 0.17rem;
}

/* ─── PC UI-SPEC §5.1.b 60×60 ───────────────────────────── */
.page-PC.day-cell {
  /* 1px 用于格间分隔线,边框走背景色变化,不用 border */
  width: 60px;
  height: 60px;
  padding: 3.9px;
}
.page-PC .day-number {
  top: 2px;
  right: 3px;
  font-size: 12px;
  line-height: 1em;
}
.page-PC .sub-label {
  line-height: 1em;
}
.page-PC .team-label {
  max-width: 50px;
  bottom: 16px;
  gap: 1px;
}
.page-PC .team-label.is-multiline {
  bottom: 8px;
}
.page-PC.variant-concert .sub-label,
.page-PC.variant-concert-hot .sub-label,
.page-PC.variant-concert-wish .sub-label {
  position: absolute;
  left: 50%;
  bottom: 16px;
  max-width: 54px;
  overflow: hidden;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  transform: translateX(-50%);
}
.page-PC .team-label.is-multiline {
  gap: 1px;
}
.page-PC .hot-icon {
  left: 0;
  top: 0;
  width: 14px;
  height: 14px;
  border-radius: 0 0 4px 0;
}
.page-PC .hot-icon::after {
  width: 8.75px;
  height: 10.5px;
}
.page-PC .wish-tag {
  width: 30px;
  height: 14px;
  border-radius: 0 0 4px 0;
  font-size: 11px;
}
</style>
